import { useQuery } from "@tanstack/react-query";

import { compareCreatedAt } from "components/helpers";
import { campaignConstants } from "constants/Campaign";
import CONSTANTS from "constants/Constants";
import { toSelectOptions } from "helpers/selectOptions";
import { filterUsersForDropdown } from "helpers/user";
import { startCase } from "helpers/string";
import { useRegionOptions } from "hooks/useRegionOptions";
import CampaignStatusPresetService from "services/CampaignStatusPreset";
import EmailProviderService from "services/EmailProviders";
import EmailTemplateService from "services/EmailTemplate";
import EmailUrlService from "services/EmailUrls";
import RevenueOpportunityRecipientPresetService from "services/RevenueOpportunityRecipientPreset";
import RevenueOpportunityStatusPresetService from "services/RevenueOpportunityStatusPreset";
import UserService from "services/User";

const STATUS_OPTIONS = toSelectOptions(campaignConstants.status);

const EMAIL_TYPE_OPTIONS = Object.entries(CONSTANTS.EMAIL_TYPES).map(
  ([key, value]) => ({
    key: value,
    value,
    text: startCase(key.toLowerCase()),
  })
);

/**
 * Option lists for the campaign form.
 *
 * `assignedUserId` keeps a deactivated but still-assigned user in the assignee
 * list, so editing a campaign does not silently drop its assignee.
 */
export function useCampaignFormOptions(assignedUserId) {
  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => UserService.getUsers(),
  });

  const { data: emailProviders = [] } = useQuery({
    queryKey: ["email-providers", { smtp: true, isSchedule: true }],
    queryFn: () => EmailProviderService.getEmailProviders(true, true),
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["email-templates", "for-filters"],
    queryFn: () => EmailTemplateService.getEmailTemplatesForFilters(),
  });

  const { data: forms = [] } = useQuery({
    queryKey: ["email-url-forms"],
    queryFn: () => EmailUrlService.getForms(),
  });

  const { data: campaignStatusPresets = [] } = useQuery({
    queryKey: ["campaign-status-presets"],
    queryFn: () => CampaignStatusPresetService.getCampaignStatusPresets(),
  });

  const { data: revenueStatusPresets = [] } = useQuery({
    queryKey: ["revenue-opportunity-status-presets"],
    queryFn: () =>
      RevenueOpportunityStatusPresetService.getCampaignStatusPresets(),
  });

  const { data: revenueRecipientPresets = [] } = useQuery({
    queryKey: ["revenue-opportunity-recipient-presets"],
    queryFn: () =>
      RevenueOpportunityRecipientPresetService.getRevenueOpportunityRecipientPresets(),
  });

  const regionOptions = useRegionOptions();

  // EmailProviderService returns an error object instead of throwing, so the
  // value must be confirmed to be an array before it is passed to a select.
  const emailServiceOptions = Array.isArray(emailProviders)
    ? emailProviders
    : [];

  return {
    statusOptions: STATUS_OPTIONS,
    emailTypeOptions: EMAIL_TYPE_OPTIONS,
    userOptions: filterUsersForDropdown(
      users,
      assignedUserId == null ? [] : [assignedUserId]
    ),
    formOptions: toSelectOptions([...forms].sort(compareCreatedAt)),
    templateOptions: toSelectOptions(templates),
    emailServiceOptions,
    regionOptions,
    campaignStatusPresetOptions: toSelectOptions(campaignStatusPresets),
    revenueStatusPresetOptions: toSelectOptions(revenueStatusPresets),
    revenueRecipientPresetOptions: toSelectOptions(revenueRecipientPresets),
  };
}
